import { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";

import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiSelect from "src/components/core-components/mui/ByzMuiSelect";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import { useAppSelector } from "src/app/hooks";
import { referenceService } from "src/api/references/referenceService";
import { useTranslate } from "src/locales";
import { toLocalISOWithMillis } from "src/utils/date/dateHelper";

interface NewBornFollowProps {
    mode?: "add" | "edit";
    recordId?: number | null;
    initialData?: any;
    onSaved?: (action?: "add" | "update") => void;
    onError?: (message?: string) => void;
    onCloseModal?: () => void;
}

//YATAY;426864;06122024
function NewBornFollow({ mode = "add", recordId, initialData, onSaved, onError, onCloseModal }: NewBornFollowProps) {
    const { t } = useTranslate();
    const { id: routePatientId } = useParams();
    const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
    const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
    const selectedPatientId = mainTabs?.[activeTabIndex]?.data?.patientId;
    const selectedUpn = mainTabs?.[activeTabIndex]?.data?.upn;

    const ptId = Number(routePatientId || selectedPatientId || 0);

    const { user } = useAppSelector((state: any) => state.auth);
    const [saving, setSaving] = useState(false);
    const [ptime, setPtime] = useState(dayjs().toDate());
    const [head, setHead] = useState("");
    const [waist, setWaist] = useState("");
    const [incubatorNumber, setIncubatorNumber] = useState("");
    const [incubatorTemp, setIncubatorTemp] = useState("");
    const [incubatorDamp, setIncubatorDamp] = useState("");
    const [incubatorChangeDate, setIncubatorChangeDate] = useState<Date | null>(null);
    const [phototherapy, setPhototherapy] = useState(false);
    const [ropDate, setRopDate] = useState<Date | null>(null);
    const [hearingDate, setHearingDate] = useState<Date | null>(null);
    const [echoDate, setEchoDate] = useState<Date | null>(null);
    const [heelDate, setHeelDate] = useState<Date | null>(null);
    const [writeOnEpicrise, setWriteOnEpicrise] = useState(false);

    const displayUser = useMemo(() => {
        return user?.username || "";
    }, [user]);

    useEffect(() => {
        if (mode === "edit" && initialData) {
            setPtime(initialData.ptime ? dayjs(initialData.ptime).toDate() : dayjs().toDate());
            setHead(initialData.head_circumference?.toString() || "");
            setWaist(initialData.waist_circumference?.toString() || "");
            setIncubatorNumber(initialData.incubator_number?.toString() || "");
            setIncubatorTemp(initialData.incubator_temperature?.toString() || "");
            setIncubatorDamp(initialData.incubator_damp?.toString() || "");
            setIncubatorChangeDate(initialData.incubator_change_date ? dayjs(initialData.incubator_change_date).toDate() : null);
            setPhototherapy(initialData.is_it_phototherapy_done === "1" || initialData.is_it_phototherapy_done === 1);
            setRopDate(initialData.rop_date ? dayjs(initialData.rop_date).toDate() : null);
            setHearingDate(initialData.hearing_test_date ? dayjs(initialData.hearing_test_date).toDate() : null);
            setEchoDate(initialData.echocardiography_date ? dayjs(initialData.echocardiography_date).toDate() : null);
            setHeelDate(initialData.heel_blood_date ? dayjs(initialData.heel_blood_date).toDate() : null);
            setWriteOnEpicrise(initialData.write_on_epicrise === "1" || initialData.write_on_epicrise === 1);
        }
    }, [mode, initialData]);

    const handleNumericInput = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        // Rakam ve virgül destekleniyor - virgülden önce en fazla 3 rakam, virgülden sonra rakamlar
        // Örnek: "123", "123,5", "12,45", ",5" gibi formatlar kabul edilir
        if (val === "" || /^\d{0,3}(,\d*)?$/.test(val)) {
            setter(val);
        }
    };

    const handleNumericKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Karakter engellemesi kaldırıldı - tüm karakterler girilebilir
    };

    const handleSave = async () => {
        if (!ptId) return;
        try {
            setSaving(true);
            const parseIntOrNull = (value: any): number | null => {
                if (value === "" || value === null || value === undefined) return null;
                const cleaned = String(value).trim();
                if (!/^\d{1,3}$/.test(cleaned)) return null;
                return parseInt(cleaned, 10);
            };

            let numericPayload: Record<string, number | null> = {};
            numericPayload = {
                head_circumference: parseIntOrNull(head),
                waist_circumference: parseIntOrNull(waist),
                incubator_number: parseIntOrNull(incubatorNumber),
                incubator_temperature: parseIntOrNull(incubatorTemp),
                incubator_damp: parseIntOrNull(incubatorDamp),
            };

            const payload = {
                pt_id: ptId,
                upn: selectedUpn ?? undefined,
                head_circumference: numericPayload.head_circumference,
                waist_circumference: numericPayload.waist_circumference,
                incubator_number: numericPayload.incubator_number,
                incubator_temperature: numericPayload.incubator_temperature,
                incubator_damp: numericPayload.incubator_damp,
                incubator_change_date: incubatorChangeDate ? toLocalISOWithMillis(dayjs(incubatorChangeDate).toDate()) : null,
                is_it_phototherapy_done: phototherapy ? "1" : "0",
                rop_date: ropDate ? toLocalISOWithMillis(dayjs(ropDate).toDate()) : null,
                hearing_test_date: hearingDate ? toLocalISOWithMillis(dayjs(hearingDate).toDate()) : null,
                echocardiography_date: echoDate ? toLocalISOWithMillis(dayjs(echoDate).toDate()) : null,
                heel_blood_date: heelDate ? toLocalISOWithMillis(dayjs(heelDate).toDate()) : null,
                write_on_epicrise: writeOnEpicrise ? "1" : "0",
                ptime: ptime ? toLocalISOWithMillis(dayjs(ptime).toDate()) : toLocalISOWithMillis(new Date()),
            };

            const isUpdate = mode === "edit" && !!recordId;

            if (isUpdate) {
                await referenceService.newbornTrace.update(recordId, {
                    ...payload,
                });
            } else {
                await referenceService.newbornTrace.add(payload);
            }

            if (onSaved) {
                onSaved(isUpdate ? "update" : "add");
            }
            if (onCloseModal) {
                onCloseModal();
            }
        } catch (error) {
            console.error("SaveNewbornTrace error:", error);
            const msg = t("card.save-error") || "Kayıt işlemi başarısız oldu.";
            if (onError) {
                onError(msg);
            } else {
                alert(msg);
            }
        } finally {
            setSaving(false);
        }
    };
    return (
        <>

            <Box sx={{ padding: '20px', width: '100%' }}>
                <Box sx={{ mb: 2 }}>
                    <ByzNurseSaveButton onClick={handleSave} loading={saving} disabled={saving} />
                </Box>
                <Grid container spacing={2}>
                    <Grid md={6} xs={6} container direction={'column'} spacing={3}>
                        <Grid md={6} xs={6} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }}>
                            <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center' }} fontWeight={500} fontSize={'14px'}>
                                {t("newborn-follow.title") || "Yenidoğan Takip"}
                            </ByzMuiTypography>
                        </Grid>

                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px', display: 'flex' }}>
                                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                                    {t("datetime") || "Tarih-Saat"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiDateTimePicker
                                    sx={{ width: 300, maxWidth: 300 }}
                                    value={ptime}
                                    onChange={(d: any) => setPtime(d)}
                                    disabled
                                    slotProps={{
                                        textField: {
                                            inputProps: { readOnly: true },
                                            sx: { width: 300, maxWidth: 300 },
                                            placeholder: ""
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                                    {t("newborn-follow.head-circumference") || "Baş çevresi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiTextField
                                    sx={{ width: 300, maxWidth: 300 }}
                                    color='info'
                                    label={t("enter") || "Giriniz"}
                                    noPlaceholder
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ inputMode: "numeric", pattern: "\\d*", step: 1 }}
                                    value={head}
                                    onChange={handleNumericInput(setHead)}
                                    onKeyDown={handleNumericKeyDown}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0', paddingRight: '10px' }}>
                                <ByzMuiTypography fontWeight={500} fontSize="14px" sx={{ marginLeft: '30px' }}>
                                    {t("newborn-follow.waist-circumference") || "Batın çevresi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiTextField
                                    sx={{ width: 300, maxWidth: 300 }}
                                    color='info'
                                    label={t("enter") || "Giriniz"}
                                    noPlaceholder
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ inputMode: "numeric", pattern: "\\d*", step: 1 }}
                                    value={waist}
                                    onChange={handleNumericInput(setWaist)}
                                    onKeyDown={handleNumericKeyDown}
                                />
                            </Grid>
                        </Grid>


                        <Grid md={6} xs={6} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }} >
                            <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center' }} fontWeight={500} fontSize={'14px'}>
                                {t("newborn-follow.incubator-section") || "Küvöz Takip"}
                            </ByzMuiTypography>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.incubator-number") || "Küvöz no"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiTextField
                                    sx={{ width: 300, maxWidth: 300 }}
                                    color='info'
                                    label={t("enter") || "Giriniz"}
                                    noPlaceholder
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ inputMode: "numeric", pattern: "\\d*", step: 1 }}
                                    value={incubatorNumber}
                                    onChange={handleNumericInput(setIncubatorNumber)}
                                    onKeyDown={handleNumericKeyDown}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.incubator-change-date") || "Küvöz değişim tarihi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiDateTimePicker
                                    sx={{ width: 300, maxWidth: 300 }}
                                    value={incubatorChangeDate}
                                    onChange={(d: any) => setIncubatorChangeDate(d)}
                                    slotProps={{
                                        textField: { 
                                            sx: { width: 300, maxWidth: 300 },
                                            placeholder: ""
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.incubator-temperature") || "Küvöz ısısı"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiTextField
                                    sx={{ width: 300, maxWidth: 300 }}
                                    color='info'
                                    label={t("enter") || "Giriniz"}
                                    noPlaceholder
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ inputMode: "numeric", pattern: "\\d*", step: 1 }}
                                    value={incubatorTemp}
                                    onChange={handleNumericInput(setIncubatorTemp)}
                                    onKeyDown={handleNumericKeyDown}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.incubator-damp") || "Küvöz nemi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiTextField
                                    sx={{ width: 300, maxWidth: 300 }}
                                    color='info'
                                    label={t("enter") || "Giriniz"}
                                    noPlaceholder
                                    variant="outlined"
                                    type="number"
                                    inputProps={{ inputMode: "numeric", pattern: "\\d*", step: 1 }}
                                    value={incubatorDamp}
                                    onChange={handleNumericInput(setIncubatorDamp)}
                                    onKeyDown={handleNumericKeyDown}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.phototherapy") || "Fototerapi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiCheckBox checked={phototherapy} onChange={(e) => setPhototherapy(e.target.checked)} />
                            </Grid>
                        </Grid>

                        <Grid md={6} xs={6} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }}>
                            <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center' }} fontWeight={500} fontSize={'14px'}>
                                {t("newborn-follow.tests-section") || "Yapılan Testler"}
                            </ByzMuiTypography>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.rop-date") || "ROP tarihi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiDateTimePicker
                                    sx={{ width: 300, maxWidth: 300 }}
                                    value={ropDate}
                                    onChange={(d: any) => setRopDate(d)}
                                    slotProps={{
                                        textField: { 
                                            sx: { width: 300, maxWidth: 300 },
                                            placeholder: ""
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.hearing-test-date") || "İşitme test tarihi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiDateTimePicker
                                    sx={{ width: 300, maxWidth: 300 }}
                                    value={hearingDate}
                                    onChange={(d: any) => setHearingDate(d)}
                                    slotProps={{
                                        textField: { 
                                            sx: { width: 300, maxWidth: 300 },
                                            placeholder: ""
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.echo-date") || "Eko tarihi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiDateTimePicker
                                    sx={{ width: 300, maxWidth: 300 }}
                                    value={echoDate}
                                    onChange={(d: any) => setEchoDate(d)}
                                    slotProps={{
                                        textField: { 
                                            sx: { width: 300, maxWidth: 300 },
                                            placeholder: ""
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("newborn-follow.heel-blood-date") || "Topuk kanı (FKU) tarihi"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} sx={{ paddingTop: '0px' }}>
                                <ByzMuiDateTimePicker
                                    sx={{ width: 300, maxWidth: 300 }}
                                    value={heelDate}
                                    onChange={(d: any) => setHeelDate(d)}
                                    slotProps={{
                                        textField: { 
                                            sx: { width: 300, maxWidth: 300 },
                                            placeholder: ""
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid md={6} xs={6} sx={{ backgroundColor: '#F4F7FE', borderBottom: '1px solid #E0E0E0' }}>
                            <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center' }} fontWeight={500} fontSize={'14px'}>
                                Diğer
                            </ByzMuiTypography>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}  >
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("height-weight.write-on-epicrise") || "Epikrizde çıksın"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} >
                                <ByzMuiCheckBox checked={writeOnEpicrise} onChange={(e) => setWriteOnEpicrise(e.target.checked)} />
                            </Grid>
                        </Grid>

                        <Grid container alignItems={'center'} justifyContent={'space-between'}>
                            <Grid md={6} xs={6} sx={{ borderBottom: '1px solid #E0E0E0' }}>
                                <ByzMuiTypography sx={{ display: 'flex', textAlign: 'center', marginLeft: '30px' }} fontWeight={500} fontSize={'14px'}>
                                    {t("user", "capitalize") || "Kullanıcı"}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid md={6} xs={6} >
                                <ByzMuiTextField
                                    sx={{ width: 300, maxWidth: 300 }}
                                    color='info'
                                    disabled
                                    label={t("user", "capitalize") || "Kullanıcı"}
                                    variant="outlined"
                                    value={displayUser}
                                    inputProps={{ readOnly: true }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default NewBornFollow;
